import { c as _c } from "react/compiler-runtime";
import type { LiHTMLAttributes } from 'react';
import { cn } from '~/utils';

/** `LiHTMLAttributes` in some TS configs omits `data-*`; allow stable E2E hooks. */
type ActivityItemCardListItemProps = Omit<LiHTMLAttributes<HTMLLIElement>, 'className' | 'children'> & {
  'data-testid'?: string;
  'data-activity-id'?: string;
};
import { ActivityListCard, ActivityListCardBody, ActivityListCardContent, ActivityListCardHeader, ActivityListCardImage } from './activity-list-card';
export const ActivityItemCard: React.FC<{
  className?: string;
  contentClassName?: string;
  bodyClassName?: string;
  headerClassName?: string;
  imageClassName?: string;
  imageImageClassName?: string;
  imageSrc: string | null;
  imageAlt: string;
  imageHref?: string | null;
  imageTarget?: React.HTMLAttributeAnchorTarget;
  title: React.ReactNode;
  topRightAction?: React.ReactNode;
  children?: React.ReactNode;
  /** `div` when wrapped in an outer `<li>` (e.g. cart). Default `li` for direct `<ul>` children. */
  listAs?: 'li' | 'div';
  /** Forwarded to the root list row (e.g. `data-testid` for E2E). */
  listItemProps?: ActivityItemCardListItemProps;
}> = t0 => {
  const $ = _c(48);
  const {
    className,
    contentClassName,
    bodyClassName,
    headerClassName,
    imageClassName,
    imageImageClassName,
    imageSrc,
    imageAlt,
    imageHref,
    imageTarget,
    title,
    topRightAction,
    children,
    listAs: t1,
    listItemProps
  } = t0;
  const listAs = t1 === undefined ? "li" : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("border-blue-100 bg-white p-3 sm:p-4", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== listItemProps) {
    t3 = listItemProps ?? {};
    $[2] = listItemProps;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== topRightAction) {
    t4 = topRightAction && <div className="absolute top-2 right-2 sm:top-3 sm:right-3">{topRightAction}</div>;
    $[4] = topRightAction;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== contentClassName) {
    t5 = cn("flex-col gap-3 sm:flex-row sm:gap-4", contentClassName);
    $[6] = contentClassName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== imageClassName) {
    t6 = cn("aspect-4/3 sm:w-30 relative w-16 shrink-0 overflow-hidden rounded-lg bg-blue-100", imageClassName);
    $[8] = imageClassName;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== imageAlt || $[11] !== imageHref || $[12] !== imageImageClassName || $[13] !== imageSrc || $[14] !== imageTarget || $[15] !== t6) {
    t7 = <ActivityListCardImage href={imageHref} src={imageSrc} alt={imageAlt} target={imageTarget} className={t6} imageClassName={imageImageClassName} />;
    $[10] = imageAlt;
    $[11] = imageHref;
    $[12] = imageImageClassName;
    $[13] = imageSrc;
    $[14] = imageTarget;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = topRightAction && "pr-8 sm:pr-10";
  let t9;
  if ($[17] !== headerClassName || $[18] !== t8) {
    t9 = cn("sm:hidden", t8, headerClassName);
    $[17] = headerClassName;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9 || $[21] !== title) {
    t10 = <ActivityListCardHeader className={t9}>{title}</ActivityListCardHeader>;
    $[20] = t9;
    $[21] = title;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t7) {
    t11 = <div className="flex items-center gap-3 sm:contents">{t7}{t10}</div>;
    $[23] = t10;
    $[24] = t7;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== bodyClassName) {
    t12 = cn("gap-1.5 sm:gap-2", bodyClassName);
    $[26] = bodyClassName;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = topRightAction && "pr-8 sm:pr-10";
  let t14;
  if ($[28] !== headerClassName || $[29] !== t13) {
    t14 = cn("hidden sm:block", t13, headerClassName);
    $[28] = headerClassName;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14 || $[32] !== title) {
    t15 = <ActivityListCardHeader className={t14}>{title}</ActivityListCardHeader>;
    $[31] = t14;
    $[32] = title;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== children || $[35] !== t12 || $[36] !== t15) {
    t16 = <ActivityListCardBody className={t12}>{t15}{children}</ActivityListCardBody>;
    $[34] = children;
    $[35] = t12;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t11 || $[39] !== t16 || $[40] !== t5) {
    t17 = <ActivityListCardContent className={t5}>{t11}{t16}</ActivityListCardContent>;
    $[38] = t11;
    $[39] = t16;
    $[40] = t5;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== listAs || $[43] !== t17 || $[44] !== t2 || $[45] !== t3 || $[46] !== t4) {
    t18 = <ActivityListCard as={listAs} className={t2} {...t3}>{t4}{t17}</ActivityListCard>;
    $[42] = listAs;
    $[43] = t17;
    $[44] = t2;
    $[45] = t3;
    $[46] = t4;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};